import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { readJsonOnce } from '../utils/safeFetch';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { toast } from 'sonner';
import {
  Mail,
  Lock,
  User,
  Loader2,
  ArrowLeft,
  Users,
  Briefcase,
  Building,
  Globe,
  Phone,
  MapPin,
  Hash,
  Eye,
  EyeOff,
  Sparkles,
  Copy,
} from 'lucide-react';
import { COUNTRIES, generateStrongPassword } from '../lib/signup-helpers';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser, login: googleLogin } = useAuth();
  
  const pendingPlan = searchParams.get('plan');
  const pendingCycle = searchParams.get('cycle') || 'monthly';
  const inviteToken = searchParams.get('invite');
  const inviteEmail = searchParams.get('email');
  const inviteMode = searchParams.get('mode'); // 'login' | 'signup'

  const applyPendingPlan = async () => {
    if (!pendingPlan) return;
    try {
      await fetch(`${API_URL}/api/subscription/select`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ plan: pendingPlan, billing_cycle: pendingCycle }),
      });
    } catch {}
  };

  const postAuthRedirect = () => {
    if (inviteToken) return `/invite/${inviteToken}`;
    if (pendingPlan) return '/pricing';
    return '/dashboard';
  };
  
  // Login state
  const [loginEmail, setLoginEmail] = useState(inviteEmail || '');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Register state
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState(inviteEmail || '');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerRole, setRegisterRole] = useState(inviteToken ? 'sales_rep' : 'sales_rep');
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerCompany, setRegisterCompany] = useState('');
  const [registerCountry, setRegisterCountry] = useState('');
  const [registerPhone, setRegisterPhone] = useState('');
  const [registerAddress, setRegisterAddress] = useState('');
  const [registerVat, setRegisterVat] = useState('');
  const [registerGdpr, setRegisterGdpr] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleGeneratePassword = () => {
    const pwd = generateStrongPassword(16);
    setRegisterPassword(pwd);
    setShowPassword(true);
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(pwd).then(
        () => toast.success('Strong password generated & copied', {
          description: 'Save it somewhere safe before submitting.',
        }),
        () => toast.success('Strong password generated', {
          description: 'Copy it from the field before submitting.',
        }),
      );
    } else {
      toast.success('Strong password generated', {
        description: 'Copy it from the field before submitting.',
      });
    }
  };

  const handleCopyPassword = () => {
    if (!registerPassword) return;
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(registerPassword).then(
        () => toast.success('Password copied'),
        () => toast.error('Could not copy'),
      );
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!loginEmail || !loginPassword) {
      toast.error('Please enter email and password');
      return;
    }
    
    setLoginLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: loginEmail,
          password: loginPassword
        })
      });

      // Read body exactly once as text, then JSON.parse only if non-empty.
      // Prevents "body stream already read" when Sentry/SW instrumentation
      // has already touched the response, and survives empty/non-JSON
      // payloads from 5xx error pages.
      const data = await readJsonOnce(response);
      if (!response.ok) {
        const detail = (data && typeof data === 'object' && data.detail) || (typeof data === 'string' ? data : null);
        throw new Error(detail || `Login failed (${response.status})`);
      }
      if (!data || typeof data !== 'object') {
        throw new Error('Unexpected response from server. Please try again.');
      }

      setUser(data);
      await applyPendingPlan();
      toast.success('Welcome back!');
      navigate(postAuthRedirect());
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!registerName || !registerEmail || !registerPassword) {
      toast.error('Please fill in all fields');
      return;
    }
    
    if (registerPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    if (!registerGdpr) {
      toast.error('Please agree to the Terms of Service and Privacy Policy to continue.');
      return;
    }
    
    setRegisterLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: registerName,
          email: registerEmail,
          password: registerPassword,
          role: registerRole,
          company: registerCompany,
          country: registerCountry,
          phone: registerPhone,
          address: registerAddress,
          vat_number: registerVat,
        })
      });

      // Read body exactly once as text, then JSON.parse only if non-empty.
      // Prevents "body stream already read" when Sentry/SW instrumentation
      // has already touched the response, and survives empty/non-JSON
      // payloads from 5xx error pages.
      const data = await readJsonOnce(response);
      if (!response.ok) {
        const detail = (data && typeof data === 'object' && data.detail) || (typeof data === 'string' ? data : null);
        throw new Error(detail || `Registration failed (${response.status})`);
      }
      if (!data || typeof data !== 'object') {
        throw new Error('Unexpected response from server. Please try again.');
      }

      setUser(data);
      await applyPendingPlan();
      toast.success('Account created successfully!');
      navigate(postAuthRedirect());
    } catch (error) {
      toast.error(error.message);
    } finally {
      setRegisterLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      {/* Background glow */}
      <div 
        className="fixed top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] pointer-events-none"
        style={{
          background: 'radial-gradient(circle at center, rgba(14, 89, 68, 0.06) 0%, transparent 70%)'
        }}
      />
      
      <div className="w-full max-w-md relative z-10">
        {/* Back button */}
        <Button
          variant="ghost"
          className="mb-6 text-muted-foreground hover:text-foreground"
          onClick={() => navigate('/')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Home
        </Button>

        {/* Logo */}
        <div className="flex items-center mb-8">
          <img src="/ludy-wordmark.png" alt="Ludy" className="h-8" />
        </div>

        <Card className="bg-card border-border/50">
          <Tabs defaultValue={inviteMode === 'signup' ? 'register' : 'login'} className="w-full">
            {inviteToken && (
              <div className="mb-4 p-3 rounded-lg border border-primary/30 bg-primary/5 text-sm text-foreground" data-testid="invite-banner">
                You have been invited to join a team. {inviteMode === 'signup' ? 'Create your account' : 'Sign in'} to continue.
              </div>
            )}
            <CardHeader className="pb-4">
              <TabsList className="grid w-full grid-cols-2 bg-secondary">
                <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Create Account</TabsTrigger>
              </TabsList>
            </CardHeader>

            <CardContent className="space-y-6">
              {/* Login Tab */}
              <TabsContent value="login" className="mt-0 space-y-4">
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="login-email"
                        type="email"
                        placeholder="you@company.com"
                        value={loginEmail}
                        onChange={(e) => setLoginEmail(e.target.value)}
                        className="pl-10 bg-background border-border"
                        data-testid="login-email"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="login-password">Password</Label>
                      <Link
                        to="/forgot-password"
                        className="text-xs text-primary hover:underline"
                        data-testid="forgot-password-link"
                      >
                        Forgot your password?
                      </Link>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="login-password"
                        type="password"
                        placeholder="••••••••"
                        value={loginPassword}
                        onChange={(e) => setLoginPassword(e.target.value)}
                        className="pl-10 bg-background border-border"
                        data-testid="login-password"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loginLoading}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="login-submit"
                  >
                    {loginLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Signing in...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </form>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <Separator className="w-full" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  onClick={googleLogin}
                  className="w-full border-border hover:bg-secondary"
                  data-testid="google-login"
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>
              </TabsContent>

              {/* Register Tab */}
              <TabsContent value="register" className="mt-0 space-y-4">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Full Name</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="register-name"
                        type="text"
                        placeholder="John Doe"
                        value={registerName}
                        onChange={(e) => setRegisterName(e.target.value)}
                        className="pl-10 bg-background border-border"
                        data-testid="register-name"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="register-email"
                        type="email"
                        placeholder="you@company.com"
                        value={registerEmail}
                        onChange={(e) => setRegisterEmail(e.target.value)}
                        className="pl-10 bg-background border-border"
                        data-testid="register-email"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label htmlFor="register-company">Company <span className="text-muted-foreground text-xs">(optional)</span></Label>
                      <div className="relative">
                        <Building className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          id="register-company"
                          type="text"
                          placeholder="Acme Inc."
                          value={registerCompany}
                          onChange={(e) => setRegisterCompany(e.target.value)}
                          className="pl-10 bg-background border-border"
                          data-testid="register-company"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="register-country">Country <span className="text-muted-foreground text-xs">(optional)</span></Label>
                      <div className="relative">
                        <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none z-10" />
                        <select
                          id="register-country"
                          value={registerCountry}
                          onChange={(e) => setRegisterCountry(e.target.value)}
                          className="h-10 w-full rounded-md border border-border bg-background pl-10 pr-3 text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-primary/50"
                          data-testid="register-country"
                        >
                          <option value="">Select country</option>
                          {COUNTRIES.map(c => (
                            <option key={c.code} value={c.name}>{c.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-phone">Phone <span className="text-muted-foreground text-xs">(optional)</span></Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="register-phone"
                        type="tel"
                        placeholder="+1 555 123 4567"
                        value={registerPhone}
                        onChange={(e) => setRegisterPhone(e.target.value)}
                        className="pl-10 bg-background border-border"
                        data-testid="register-phone"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-address">Company Address <span className="text-muted-foreground text-xs">(optional)</span></Label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="register-address"
                        type="text"
                        placeholder="123 Main St, Suite 400, New York NY 10001"
                        value={registerAddress}
                        onChange={(e) => setRegisterAddress(e.target.value)}
                        className="pl-10 bg-background border-border"
                        data-testid="register-address"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="register-vat">VAT / Tax ID <span className="text-muted-foreground text-xs">(optional)</span></Label>
                    <div className="relative">
                      <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="register-vat"
                        type="text"
                        placeholder="EU123456789 or EIN"
                        value={registerVat}
                        onChange={(e) => setRegisterVat(e.target.value)}
                        className="pl-10 bg-background border-border"
                        data-testid="register-vat"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="register-password">Password</Label>
                      <button
                        type="button"
                        onClick={handleGeneratePassword}
                        className="text-xs text-primary hover:underline inline-flex items-center gap-1"
                        data-testid="generate-password-btn"
                      >
                        <Sparkles className="w-3 h-3" />
                        Generate strong password
                      </button>
                    </div>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="register-password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Min. 6 characters"
                        value={registerPassword}
                        onChange={(e) => setRegisterPassword(e.target.value)}
                        className="pl-10 pr-20 bg-background border-border"
                        data-testid="register-password"
                      />
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                        {registerPassword && (
                          <button
                            type="button"
                            onClick={handleCopyPassword}
                            className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                            title="Copy password"
                            data-testid="copy-password-btn"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setShowPassword(v => !v)}
                          className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                          title={showPassword ? 'Hide password' : 'Show password'}
                          data-testid="toggle-password-visibility"
                        >
                          {showPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3">
                    <Label>I am a</Label>
                    <RadioGroup
                      value={registerRole}
                      onValueChange={setRegisterRole}
                      className="grid grid-cols-2 gap-4"
                    >
                      <div>
                        <RadioGroupItem
                          value="sales_rep"
                          id="role-rep"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="role-rep"
                          className="flex flex-col items-center justify-center rounded-lg border-2 border-border bg-background p-4 hover:bg-secondary cursor-pointer peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5"
                          data-testid="role-rep"
                        >
                          <Briefcase className="w-6 h-6 mb-2 text-primary" />
                          <span className="text-sm font-medium">Sales Rep</span>
                          <span className="text-xs text-muted-foreground">Individual contributor</span>
                        </Label>
                      </div>
                      <div>
                        <RadioGroupItem
                          value="manager"
                          id="role-manager"
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor="role-manager"
                          className="flex flex-col items-center justify-center rounded-lg border-2 border-border bg-background p-4 hover:bg-secondary cursor-pointer peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5"
                          data-testid="role-manager"
                        >
                          <Users className="w-6 h-6 mb-2 text-primary" />
                          <span className="text-sm font-medium">Manager</span>
                          <span className="text-xs text-muted-foreground">Lead a team</span>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* GDPR / ToS consent — required for account creation */}
                  <div className="flex items-start gap-2 pt-1">
                    <Checkbox
                      id="register-gdpr"
                      checked={registerGdpr}
                      onCheckedChange={setRegisterGdpr}
                      className="mt-0.5"
                      data-testid="register-gdpr-checkbox"
                    />
                    <Label
                      htmlFor="register-gdpr"
                      className="text-xs text-muted-foreground leading-relaxed cursor-pointer font-normal"
                    >
                      I agree to Ludy's{' '}
                      <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        Terms of Service
                      </a>{' '}
                      and{' '}
                      <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                        Privacy Policy
                      </a>
                      , and consent to the processing of my personal data in line with GDPR.
                    </Label>
                  </div>

                  <Button
                    type="submit"
                    disabled={registerLoading}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                    data-testid="register-submit"
                  >
                    {registerLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating account...
                      </>
                    ) : (
                      'Create Account'
                    )}
                  </Button>
                </form>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <Separator className="w-full" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-card px-2 text-muted-foreground">Or continue with</span>
                  </div>
                </div>

                <Button
                  type="button"
                  variant="outline"
                  onClick={googleLogin}
                  className="w-full border-border hover:bg-secondary"
                >
                  <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24">
                    <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continue with Google
                </Button>
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>

        <p className="text-center text-xs text-muted-foreground mt-6">
          By continuing, you agree to our Terms of Service and Privacy Policy.
        </p>
      </div>
    </div>
  );
}
